import jsQR from 'jsqr';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DimensionValue, Pressable, StyleSheet, Text, View } from 'react-native';
import Webcam from 'react-webcam';
import CardSlashIcon from './Icons/CardSlashIcon';

interface ICamera {
  setImage: (image: string | null | undefined) => void;
}

/**
 * @description Camera component for scanning QR codes
 * @param {ICamera} { setImage }
 * @return {React.FC}
 */
const Camera: React.FC<ICamera> = ({ setImage }) => {
  const webcamRef = useRef<Webcam>(null);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const scanQrCode = () => {
    const video = webcamRef.current?.video;
    if (video) {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const image = canvas.getContext('2d');
      if (image) {
        image.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = image.getImageData(0, 0, canvas.width, canvas.height);
        const qrcode = jsQR(imageData.data, canvas.width, canvas.height);

        if (qrcode) {
          setImage(qrcode.data);
        }
      }
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (cameraEnabled) {
      interval = setInterval(scanQrCode, 500);
    }
    return () => {
      clearInterval(interval);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraEnabled]);

  const handleUserMediaError = () => {
    setCameraEnabled(false);
    setErrorMessage('Please allow camera access in your browser settings and try again');
  };

  const retry = useCallback(async () => {
    setCameraEnabled(true);
    setErrorMessage('');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: 'environment'
  };
  return (
    <>
      {cameraEnabled ? (
        <View
          style={{
            flex: 1,
            justifyContent: 'center'
          }}>
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            videoConstraints={videoConstraints}
            onUserMediaError={handleUserMediaError}
            style={{
              width: '100%'
            }}
          />
          <View style={styles.qrReticle} />
        </View>
      ) : (
        <View style={styles.emptyListStyle}>
          <View style={{ flex: 1, justifyContent: 'flex-end' }}>
            <CardSlashIcon iconWidth={140} iconHeight={140}></CardSlashIcon>
          </View>

          <View>
            <View style={styles.emptyListTextStyle}>
              <Text style={styles.emptyListMessageTitleStyle}>Can't find a camera</Text>
              <Text style={styles.emptyListMessageSubtitleStyle}>{errorMessage}</Text>
            </View>
          </View>

          <View style={{ flex: 1, marginTop: 16 }}>
            <Pressable style={styles.onboardButton} role="button" onPress={retry}>
              <Text style={styles.onboardButtonText}>Retry Camera</Text>
            </Pressable>
          </View>
        </View>
      )}
    </>
  );
};

export default Camera;

const styles = StyleSheet.create({
  emptyListStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 36,
    flex: 1,
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    padding: 36
  },
  emptyListTextStyle: {
    height: '100vh' as DimensionValue,
    justifyContent: 'center',
    alignItems: 'center'
  },
  pickerContainer: {
    position: 'absolute',
    bottom: 20,
    left: '50%',
    transform: [{ translateX: '-50%' }],
    zIndex: 10,
    width: '80%',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderRadius: 8,
    padding: 10
  },
  controls: {
    position: 'absolute',
    bottom: 110,
    width: '100%',
    zIndex: 20,
    display: 'flex',
    alignItems: 'center',
    gap: 12
  },
  emptyListMessageTitleStyle: {
    fontSize: 20,
    fontWeight: 700,
    textAlign: 'center'
  },
  emptyListMessageSubtitleStyle: {
    fontSize: 18,
    color: '#999',
    textAlign: 'center'
  },
  onboardButton: {
    paddingTop: 16,
    paddingRight: 24,
    paddingBottom: 16,
    paddingLeft: 24,
    backgroundColor: 'rgb(41, 45, 50)',
    borderRadius: 6,
    color: '#fff',
    textAlign: 'center',
    boxShadow: '1px 2px 4px rgba(41, 45, 50, 0.5)'
  },
  onboardButtonText: {
    color: 'inherit',
    fontSize: 16
  },
  cameraControls: {
    position: 'absolute',
    left: 0,
    bottom: 0,
    width: '100%',
    padding: 24
  },
  cameraButton: {
    padding: 16,
    backgroundColor: '#000',
    borderRadius: 6
  },
  cameraButtonText: {
    color: '#fff',
    fontWeight: 700,
    textAlign: 'center'
  },
  qrReticle: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '70%',
    height: '40%',
    borderWidth: 3,
    borderColor: '#fff',
    transform: [{ translateX: '-50%' }, { translateY: '-50%' }],
    zIndex: 30,
    borderRadius: 8
  }
});
